import { defineStore } from "pinia";
import NotificationItemsApi from "@/services/api/notificationItemsApi";
import { getCompanyId, getUserId } from "@/utils/userUtils";
import { formatDateOnly } from "@/utils/dateUtils";

// Страница и popup используют одинаковые действия, но не разделяют записи и пагинацию.
const createNotificationListStore = (id, listParams) => defineStore(id, {
  state: () => ({
    items: [],
    pagination: null,
    dataIsLoading: false,
    loadFailed: false,
    revision: 0,
    companyId: null,
    userId: null,
    activePage: null,
    firstPage: 1,
    isLoadingMore: false,
    isRefreshing: false,
    loadMoreFailed: false,
    appendRevision: 0,
    readingIds: [],
    readingAll: false,
    readRevision: 0,
  }),

  getters: {
    isReading: (state) => state.readingAll || state.readingIds.length > 0,
    hasMore: (state) => Boolean(state.pagination?.next),
    groups: (state) => {
      const groups = [];
      for (const item of state.items) {
        const date = formatDateOnly(item.created_at);
        let group = groups[groups.length - 1];
        if (!group || group.date !== date) {
          group = { date, items: [] };
          groups.push(group);
        }
        group.items.push(item);
      }
      return groups;
    },
  },

  actions: {
    reset() {
      this.readRevision += 1;
      this.readingIds = [];
      this.readingAll = false;
      this.activePage = null;
      this.clearItems();
    },

    clearItems() {
      this.appendRevision += 1;
      this.isLoadingMore = false;
      this.isRefreshing = false;
      this.loadMoreFailed = false;
      this.revision += 1;
      this.items = [];
      this.pagination = null;
      this.dataIsLoading = false;
      this.loadFailed = false;
      this.companyId = null;
      this.userId = null;
    },

    isCurrent(revision) {
      return revision === this.revision &&
        this.companyId === getCompanyId() &&
        this.userId === getUserId();
    },

    openPage(page = 1) {
      this.reset();
      this.firstPage = Number(page);
      this.activePage = page;
      return this.fetchItems(page);
    },

    refreshIfOpen() {
      if (this.activePage === null || !this.isCurrent(this.revision)) return;

      return this.fetchItems(this.activePage, { background: true });
    },

    mergeItems(existing, incoming) {
      const items = new Map(existing.map((item) => [item.id, item]));
      incoming.forEach((item) => items.set(item.id, item));
      return [...items.values()].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at) || b.id - a.id
      );
    },

    async loadMore() {
      if (listParams.unread_only || !this.hasMore || this.isLoadingMore || this.isRefreshing || this.dataIsLoading ||
          this.activePage === null || !this.isCurrent(this.revision)) return;

      const revision = this.revision;
      const appendRevision = ++this.appendRevision;
      const page = this.pagination.next;
      this.isLoadingMore = true;
      this.loadMoreFailed = false;
      try {
        const response = await NotificationItemsApi.index(this.companyId, { page, ...listParams });
        if (!this.isCurrent(revision) || appendRevision !== this.appendRevision) return;

        this.items = this.mergeItems(this.items, response.data.result);
        this.pagination = response.data.pagination_meta;
        this.activePage = page;
      } catch (error) {
        if (!this.isCurrent(revision) || appendRevision !== this.appendRevision) return;
        if ([401, 403, 404].includes(error.response?.status)) {
          this.clearItems();
          this.loadFailed = true;
        } else {
          this.loadMoreFailed = true;
        }
      } finally {
        if (appendRevision === this.appendRevision) this.isLoadingMore = false;
      }
    },

    markRead(id = null) {
      return this.setReadState({ id, read: true });
    },

    markUnread(id) {
      if (!id) return false;
      return this.setReadState({ id, read: false });
    },

    async setReadState({ id, read }) {
      if (this.activePage === null || !this.isCurrent(this.revision)) return false;
      if (this.readingAll || this.readingIds.includes(id) || (id === null && this.isReading)) return false;

      const companyId = this.companyId;
      const userId = this.userId;
      const revision = this.readRevision;
      const isCurrent = () => revision === this.readRevision &&
        companyId === getCompanyId() && userId === getUserId();
      if (id === null) this.readingAll = true;
      else this.readingIds.push(id);
      try {
        if (id === null) await NotificationItemsApi.markAllRead(companyId);
        else if (read) await NotificationItemsApi.markRead(companyId, id);
        else await NotificationItemsApi.markUnread(companyId, id);
        if (!isCurrent()) return false;

        // Не обнуляем список или badge: во время PATCH могли прийти новые записи.
        await Promise.all([
          useNotificationItemsStore().refreshIfOpen(),
          useNotificationPopupStore().refreshIfOpen(),
        ]);
        return isCurrent();
      } catch (error) {
        // Ошибку действия показывает API-interceptor; повтор остаётся доступен.
        return false;
      } finally {
        if (isCurrent()) {
          if (id === null) this.readingAll = false;
          else this.readingIds = this.readingIds.filter((readingId) => readingId !== id);
        }
      }
    },

    async fetchItems(page = 1, { background = false } = {}) {
      if (!background) this.clearItems();
      else this.revision += 1;
      this.companyId = getCompanyId();
      this.userId = getUserId();
      if (!this.companyId || !this.userId) return;

      const revision = this.revision;
      this.dataIsLoading = !background || this.dataIsLoading;
      this.isRefreshing = background;
      try {
        // Live перечитывает все загруженные страницы, не удаляя хвост, сдвинутый новыми записями.
        const refreshHistory = background && !listParams.unread_only && this.items.length > 0 &&
          (this.firstPage === 1 || page > this.firstPage);
        const responses = [await NotificationItemsApi.index(this.companyId, {
          page: refreshHistory ? this.firstPage : page,
          ...listParams,
        })];
        // При сдвиге offset дочитываем до прежнего хвоста: без дыр и устаревшего read_at.
        if (refreshHistory) {
          const oldest = this.items[this.items.length - 1];
          let last = responses[responses.length - 1];
          let edge = last.data.result[last.data.result.length - 1];
          // Идём по свежему next: очистка могла уменьшить количество страниц.
          while (last.data.pagination_meta.next && (last.data.pagination_meta.page < page ||
                 (oldest && edge &&
                  (new Date(edge.created_at) - new Date(oldest.created_at) || edge.id - oldest.id) > 0))) {
            if (!this.isCurrent(revision)) return;
            last = await NotificationItemsApi.index(this.companyId, { page: last.data.pagination_meta.next, ...listParams });
            responses.push(last);
            edge = last.data.result[last.data.result.length - 1];
          }
        }
        // Поздний ответ другой страницы, компании или сессии не заменяет текущий список.
        if (!this.isCurrent(revision)) return;

        const incoming = responses.flatMap((response) => response.data.result);
        // Перечитанный диапазон заменяет старый: удалённые Items не должны воскресать.
        this.items = refreshHistory ? this.mergeItems([], incoming) : incoming;
        this.pagination = responses[responses.length - 1].data.pagination_meta;
        if (refreshHistory) this.activePage = this.pagination.page;
        this.loadFailed = false;
      } catch (error) {
        if (this.isCurrent(revision)) {
          this.items = [];
          this.pagination = null;
          this.loadFailed = true;
        }
        // Сообщение ошибки показывает общий API-interceptor.
      } finally {
        if (this.isCurrent(revision)) {
          this.dataIsLoading = false;
          this.isRefreshing = false;
        }
      }
    },
  },
});

export const useNotificationItemsStore = createNotificationListStore("notificationItems", { limit: 40 });
export const useNotificationPopupStore = createNotificationListStore("notificationPopup", { limit: 5, unread_only: true });
